import {
  injectJsonLd,
  removeJsonLd,
  setMetaTags,
  type PageMetaOptions,
} from './meta';
import { createCategorySlug, createSoftwareSlug } from './urlHelpers';

export const SITE_ORIGIN = 'https://kitapcik.net';

export function absoluteUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_ORIGIN}${p}`;
}

export function applyPageSeo(options: PageMetaOptions): void {
  setMetaTags({
    ...options,
    canonical: options.canonical
      ? options.canonical.startsWith('http')
        ? options.canonical
        : absoluteUrl(options.canonical)
      : undefined,
  });
}

export type BreadcrumbItem = { name: string; path: string };

export function setBreadcrumbJsonLd(items: BreadcrumbItem[]): void {
  if (items.length === 0) {
    removeJsonLd('breadcrumb');
    return;
  }
  injectJsonLd(
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path),
      })),
    },
    'breadcrumb'
  );
}

export function setForumJsonLd(forum: {
  name: string;
  forum_id: string;
  description?: string | null;
  url?: string | null;
  logo_url?: string | null;
  category: string;
  members_count: number;
  topics_count: number;
  posts_count: number;
}): void {
  const pageUrl = absoluteUrl(`/forum/${forum.forum_id}`);
  const desc =
    forum.description ||
    `${forum.name} forum istatistikleri: ${forum.members_count.toLocaleString('tr-TR')} üye, ${forum.topics_count.toLocaleString('tr-TR')} konu.`;

  injectJsonLd(
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': pageUrl,
      url: pageUrl,
      name: `${forum.name} Forum İstatistikleri`,
      description: desc,
      inLanguage: 'tr-TR',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Türk Forum İstatistikleri',
        url: SITE_ORIGIN,
      },
      about: {
        '@type': 'Organization',
        name: forum.name,
        url: forum.url || pageUrl,
        ...(forum.logo_url ? { logo: forum.logo_url } : {}),
      },
      mainEntity: {
        '@type': 'WebSite',
        name: forum.name,
        url: forum.url || pageUrl,
        interactionStatistic: [
          {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/JoinAction',
            userInteractionCount: forum.members_count,
          },
          {
            '@type': 'InteractionCounter',
            interactionType: 'https://schema.org/CommentAction',
            userInteractionCount: forum.posts_count,
          },
        ],
      },
    },
    'page'
  );
}

export function setCategoryListJsonLd(categoryName: string, forumCount: number): void {
  const url = absoluteUrl(`/category/${createCategorySlug(categoryName)}`);
  injectJsonLd(
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${categoryName} forumları`,
      description: `${categoryName} kategorisinde ${forumCount} forum listeleniyor.`,
      url,
      numberOfItems: forumCount,
    },
    'page'
  );
}

export function categoryCanonical(categoryName: string): string {
  return absoluteUrl(`/category/${createCategorySlug(categoryName)}`);
}

export function softwareCanonical(softwareName: string): string {
  return absoluteUrl(`/software/${createSoftwareSlug(softwareName)}`);
}

export function forumCanonical(forumId: string): string {
  return absoluteUrl(`/forum/${forumId}`);
}
